import { useEffect } from "react";
import { asyncTaskResult } from "@/network/network";
import { useAsyncTasks } from "@/modern/contexts/async-task-handler";
import { useIntegrationErrorReporter } from "./useIntegrationErrorReporter";
import type { IntegrationProvider } from "./types";

type UseAsyncTaskFailureReporterOptions<TTaskType extends string> = {
  groupKey: number | null | undefined;
  taskTypes: TTaskType[];
  provider: IntegrationProvider;
  fallback: string;
};

export function useAsyncTaskFailureReporter<TTaskType extends string>({
  groupKey,
  taskTypes,
  provider,
  fallback,
}: UseAsyncTaskFailureReporterOptions<TTaskType>) {
  const { onTaskEvent } = useAsyncTasks<TTaskType, string>();
  const { reportError } = useIntegrationErrorReporter();

  useEffect(() => {
    if (!groupKey) return undefined;

    const unsubscribers = taskTypes.map((taskType) =>
      onTaskEvent(groupKey, taskType, "failed", async (task) => {
        try {
          const response = await asyncTaskResult(Number(task.id));
          reportError({ error: response, provider, fallback });
        } catch (error) {
          reportError({ error, provider, fallback });
        }
      })
    );

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [groupKey, taskTypes, provider, fallback, onTaskEvent, reportError]);
}
